/**
 * KitFormat
 * All number and date format related utilities
 */

import { hasValue } from './KitData';
import { getCountryCodeFromLocale, getCurrencyCode, getLocale, parseLocale } from './KitLocale';

// Number formatting options for various types of data
// Note: These formats came from a discussion with Jim Solomon regarding significant digits that should be displayed in the Fleet-Depot app.
// Depending on the audience of any particular application, there might be difference in how we want to display the number of significant digits
export const DataTypeFormatDefaults: Record<string, Intl.NumberFormatOptions> = {
  distance: { minimumFractionDigits: 0, maximumFractionDigits: 1 }, // distance = miles || kilometers
  energy: { minimumFractionDigits: 2, maximumFractionDigits: 4 },
  percent: { minimumFractionDigits: 0, maximumFractionDigits: 1, style: 'percent' },
  power: { minimumFractionDigits: 0, maximumFractionDigits: 3 },
};

// Number formatting wrapper functions that minimize the boilerplate
export const formatNumber = (num: number, opts?: Intl.NumberFormatOptions): number | string => {
  const fmt = Intl.NumberFormat([getLocale()], opts ?? {});
  if (hasValue(num) && !Number.isNaN(num)) {
    return fmt.format(num);
  }
  return num ?? '--';
};

export const formatPercent = (value: number, opts?: Intl.NumberFormatOptions): string => {
  const formatOptions: Intl.NumberFormatOptions = {
    style: 'percent',
    maximumFractionDigits: 2,
  };
  const loc = getLocale();
  const formattedPercentage = Intl.NumberFormat(loc, opts ?? formatOptions);
  return value ? formattedPercentage.format(value * 0.01) : '--';
};

export const formatCurrency = (num: number): string | unknown => {
  const loc = getLocale();
  const { countryCode } = parseLocale(loc);
  const fmt = Intl.NumberFormat(loc, { style: 'currency', currency: getCurrencyCode(countryCode) });
  if (!Number.isNaN(num)) {
    return fmt.format(num);
  }
  return num ?? '--';
};

export const formatTelephone = (str: string, countryCode?: string): string => {
  const patterns: { [countryCode: string]: Record<string, string | RegExp> } = {
    // (XXX) XXX-XXXX
    US: { regex: /(\d{3})(\d{3})(\d{4})/, format: '($1) $2-$3' },
  };

  const pattern = patterns[countryCode ?? getCountryCodeFromLocale(getLocale())];

  if (str) {
    return pattern ? str.replace(pattern.regex, pattern.format as string) : str;
  }

  return str;
};

/**
 * Formats a duration from seconds:
 * 3h 23m 10s
 *
 * @param seconds
 * @param locale
 * @returns string
 */
export const formatDurationFromSeconds = (seconds: number, locale: string): string => {
  const localeToUse = locale ?? getLocale();

  const secondsFormatter = new Intl.NumberFormat(localeToUse, {
    style: 'unit',
    unit: 'second',
    unitDisplay: 'narrow',
  } as Intl.NumberFormatOptions);

  const minutesFormatter = new Intl.NumberFormat(localeToUse, {
    style: 'unit',
    unit: 'minute',
    unitDisplay: 'narrow',
  } as Intl.NumberFormatOptions);

  const hoursFormatter = new Intl.NumberFormat(localeToUse, {
    style: 'unit',
    unit: 'hour',
    unitDisplay: 'narrow',
  } as Intl.NumberFormatOptions);

  const daysFormatter = new Intl.NumberFormat(localeToUse, {
    style: 'unit',
    unit: 'day',
    unitDisplay: 'narrow',
  } as Intl.NumberFormatOptions);

  let secondsBeingCalculated = seconds;

  const days = Math.floor(seconds / 86400);
  if (days > 0) {
    secondsBeingCalculated -= days * 86400;
  }

  const hours = Math.floor(secondsBeingCalculated / 3600);
  if (hours > 0) {
    secondsBeingCalculated -= hours * 3600;
  }

  const minutes = Math.floor(secondsBeingCalculated / 60);
  if (minutes > 0) {
    secondsBeingCalculated -= minutes * 60;
  }
  const displayArr: string[] = [];

  if (days) {
    displayArr.push(daysFormatter.format(days));
  }

  if (hours) {
    displayArr.push(hoursFormatter.format(hours));
  }
  if (minutes) {
    displayArr.push(minutesFormatter.format(minutes));
  }
  if (secondsBeingCalculated || secondsBeingCalculated === 0) {
    displayArr.push(secondsFormatter.format(Math.floor(secondsBeingCalculated)));
  }
  return displayArr.join(' ');
};

export default {
  formatCurrency,
  formatDurationFromSeconds,
  formatNumber,
  formatPercent,
  formatTelephone,
};
